import React, { useCallback, useState, useRef, useEffect } from "react";
// import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Save,
  Download,
  Upload,
  Eye,
  X,
  History,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";
// ---------------------------------------------------------------------------
// FormIO runtime — legacy v4 (formiojs@4.x), mounted directly via DOM refs.
//
// We deliberately stopped using `@formio/react`'s `<FormBuilder>` / `<Form>`
// React wrappers and the matching `@formio/js@5.x` runtime. With that stack
// the component-edit dialog inside FormBuilder froze on every interaction
// (Save / Cancel / X / typing into a field): clicks landed but no listener
// fired and no log appeared. We tried CSS overlay cleanup, a
// MutationObserver, removing StrictMode, stabilizing the schema prop, and a
// React 19 → React 18 downgrade — none of it changed the outcome.
//
// This file now uses formiojs v4 directly. We instantiate `FormBuilder` /
// `Formio.createForm` on a plain DOM element via a ref. React owns only the
// container element; FormIO owns everything inside. There's no wrapper
// reactivity, no `initialForm` prop reapplication, no React reconciliation
// touching the builder DOM mid-flight. Schema changes from outside (AI
// assistant, version restore, import) are pushed in imperatively with
// `builder.setForm(...)`.
// ---------------------------------------------------------------------------
// @ts-ignore — formiojs v4 ships without first-class TS types
import { Formio, FormBuilder as FormioFormBuilder } from "formiojs";
import {
  createForm,
  updateForm,
  saveFormContent,
  getFormById,
  getFormContent,
  getFormVersions,
} from "../../services/formService";
import type { Form, FormContent } from "../../services/formService";
import { registerCustomComponents } from "../components/formio-custom";
import FormBuilderSidebar from "../components/studio/FormBuilderSidebar";
import VersionHistoryModal from "../components/studio/VersionHistoryModal";

// formiojs v4 stylesheets. `formio.full.min.css` is already imported globally
// from `src/index.css`; we re-import the builder-specific sheet here for
// completeness when this page is the only consumer.
// @ts-ignore
import "formiojs/dist/formio.builder.min.css";
import "./FormBuilder.css";

// Align with old doit-ui: no /project/standalone (or any project) API call when opening a Form.
// Form.io builder expects a projectId; we no-op loadProject() and set a placeholder URL so no request is made.
(function initFormioStandalone() {
  if (typeof Formio === "undefined" || typeof window === "undefined") return;
  try {
    const proto = (Formio as any).prototype;
    if (proto && typeof proto.loadProject === "function") {
      proto.loadProject = function () {
        return Promise.resolve(null);
      };
    }
  } catch (_) { }
  // Placeholder so builder gets a projectId; loadProject is no-op so this URL is never requested
  (Formio as any).setProjectUrl("https://standalone.local/project/standalone");
})();

interface FormBuilderPageProps {
  formId?: string; // If editing an existing form
  onBack: () => void;
  onSave?: (form: Form) => void;
  onFormChange?: (json: string) => void;
  onAiClick?: () => void;
  initialFormJson?: string;
}

// Form.io schema type
// Using any to establish compatibility with the library's loose types
type FormioSchema = any;

const FormBuilderPage: React.FC<FormBuilderPageProps> = ({
  formId,
  onBack,
  onSave,
  onFormChange,
  onAiClick,
  initialFormJson,
}) => {
  // Form metadata
  const [formName, setFormName] = useState("");
  const [formDescription, setFormDescription] = useState("");

  // Form.io schema. This is React's view of the schema — it lags slightly
  // behind the live builder (we sync from the builder's `change` event).
  // The builder's authoritative copy lives inside FormIO; we use this
  // state only for the save flow and the preview modal.
  const [formSchema, setFormSchema] = useState<FormioSchema>({
    display: "form",
    components: [],
  });

  // UI state
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [existingForm, setExistingForm] = useState<Form | null>(null);
  const [versions, setVersions] = useState<FormContent[]>([]);
  const [currentVersion, setCurrentVersion] = useState<string>("");
  const [isVersionModalOpen, setIsVersionModalOpen] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const loadCancelledRef = useRef(false);
  const skipNextOnFormChangeRef = useRef(false);

  // ---------------------------------------------------------------------
  // Direct-mount FormIO builder — refs declared up-front so the
  // `addComponentToBuilder` callback (below) closes over already-
  // initialised values rather than relying on TDZ-safe forward references.
  // ---------------------------------------------------------------------
  const builderHostRef = useRef<HTMLDivElement | null>(null);
  // The actual FormBuilder instance. v4 returns an object with `.instance`
  // (the underlying form) and `.destroy()`.
  const builderRef = useRef<any>(null);
  // Latest schema we pushed into the builder. We compare against incoming
  // initialFormJson by JSON-string so we don't push redundantly.
  const builderSchemaJsonRef = useRef<string>("");
  // Ref-cached versions of state we read from inside FormIO callbacks so
  // we don't have to re-mount the builder when they change.
  const formSchemaRef = useRef<FormioSchema>(formSchema);
  useEffect(() => {
    formSchemaRef.current = formSchema;
  }, [formSchema]);

  /**
   * Append a component to the live FormIO builder. Used by both the
   * native capture-phase drop handler (drops inside the builder DOM)
   * and the React onDrop on the outer wrapper (drops on padding around
   * the builder). We try the FormIO-native `WebformBuilder.addComponent`
   * first because it routes through the builder's own redraw pipeline;
   * falls back to a full schema swap via `setForm` if that API isn't
   * exposed in this build of formiojs.
   */
  const addComponentToBuilder = useCallback((component: any) => {
    const debug = (...args: any[]) => {
      if (typeof window !== "undefined" && (window as any).__doitDebugFormio) {
        // eslint-disable-next-line no-console
        console.log("[FormBuilder/add]", ...args);
      }
    };

    const builder = builderRef.current;
    if (!builder) {
      debug("no live builder — falling back to React state");
      setFormSchema((prev: any) => ({
        ...prev,
        components: [...(prev.components || []), component],
      }));
      return;
    }

    Promise.resolve(builder.ready)
      .then(() => {
        const instance = builder.instance ?? builder.webform ?? builder;
        if (instance && typeof instance.addComponent === "function") {
          try {
            instance.addComponent(component, instance, undefined, false);
            debug("added via instance.addComponent", component?.type);
            return;
          } catch (err) {
            debug("instance.addComponent threw, trying single-arg", err);
            try {
              instance.addComponent(component);
              debug("added via instance.addComponent (single-arg)", component?.type);
              return;
            } catch (err2) {
              debug("instance.addComponent (single-arg) also threw", err2);
              /* fall through to setForm */
            }
          }
        }

        const current =
          builder.schema ??
          builder.instance?.schema ?? {
            display: "form",
            components: [],
          };
        const next = JSON.parse(JSON.stringify(current));
        next.components = [...(next.components || []), component];
        builderSchemaJsonRef.current = JSON.stringify(next);
        if (typeof builder.setForm === "function") {
          builder.setForm(next);
          debug("added via builder.setForm", component?.type);
        } else if (typeof builder.instance?.setForm === "function") {
          builder.instance.setForm(next);
          debug("added via builder.instance.setForm", component?.type);
        } else {
          console.error("[FormBuilder] no setForm available on builder");
        }
        setFormSchema(next);
      })
      .catch((err) => {
        console.error("[FormBuilder] ready rejected during add", err);
      });
  }, []);

  // Load existing form if editing. Defer run so Strict Mode cleanup cancels the first run before any request.
  useEffect(() => {
    if (!formId) return;
    loadCancelledRef.current = false;
    const run = async () => {
      if (loadCancelledRef.current) return;
      setIsLoading(true);
      try {
        const form = await getFormById(formId);
        if (loadCancelledRef.current) return;
        setExistingForm(form);
        setFormName(form.name);
        setFormDescription(form.description || "");
        const versionToFetch = form.version || "0.1";
        if (form.nameSlug) {
          const content = await getFormContent(form.nameSlug, versionToFetch);
          if (loadCancelledRef.current) return;
          if (content.formioJson) {
            const decodedSchema = JSON.parse(atob(content.formioJson));
            setFormSchema(decodedSchema);
          }
          setCurrentVersion(versionToFetch);
          try {
            const allVersions = await getFormVersions(form.nameSlug);
            if (loadCancelledRef.current) return;
            setVersions(allVersions);
          } catch (e) {
            if (!loadCancelledRef.current) console.error("Error loading form versions:", e);
          }
        } else {
          if (!loadCancelledRef.current) console.warn("Form has no nameSlug, cannot fetch content", form);
        }
      } catch (error) {
        if (!loadCancelledRef.current) {
          console.error("Error loading form:", error);
          toast.error("Failed to load form");
        }
      } finally {
        if (!loadCancelledRef.current) setIsLoading(false);
      }
    };
    const t = window.setTimeout(run, 0);
    return () => {
      loadCancelledRef.current = true;
      window.clearTimeout(t);
    };
  }, [formId]);

  // Sync internal schema when parent (AI) modifies it
  useEffect(() => {
    if (initialFormJson) {
      try {
        const schema = JSON.parse(initialFormJson);
        const currentStr = JSON.stringify(formSchema);
        if (initialFormJson !== currentStr) {
          skipNextOnFormChangeRef.current = true; // avoid feedback loop: don't echo back to parent
          setFormSchema(schema);
        }
      } catch (e) {
        console.error("Failed to parse incoming form JSON", e);
      }
    }
  }, [initialFormJson]);

  // Notify parent of internal schema changes (skip when update came from parent/initialFormJson)
  useEffect(() => {
    if (skipNextOnFormChangeRef.current) {
      skipNextOnFormChangeRef.current = false;
      return;
    }
    onFormChange?.(JSON.stringify(formSchema));
  }, [formSchema, onFormChange]);

  // Dynamically load Bootstrap CSS for Form.io
  useEffect(() => {
    // Register custom components
    registerCustomComponents();

    const link = document.createElement("link");
    link.href =
      "https://cdn.jsdelivr.net/npm/bootstrap@4.6.2/dist/css/bootstrap.min.css";
    link.rel = "stylesheet";
    link.id = "bootstrap-css"; // ID to identify and remove later
    document.head.appendChild(link);

    return () => {
      const existingLink = document.getElementById("bootstrap-css");
      if (existingLink) {
        document.head.removeChild(existingLink);
      }
    };
  }, []);

  // ---------------------------------------------------------------------
  // Mount / un-mount the FormIO v4 FormBuilder on builderHostRef
  // ---------------------------------------------------------------------
  // We re-mount whenever the *identity* of the form changes — i.e. the
  // formId (switching between forms) or the builder mode (Custom vs
  // Default rewires our wrapper CSS class). We do NOT re-mount on every
  // schema state change — pushing schema updates uses `setForm(...)`
  // instead. Loading state gates the initial mount so that the schema
  // is ready before FormIO renders.
  useEffect(() => {
    if (isLoading) return;
    const host = builderHostRef.current;
    if (!host) return;

    // Defensive: clear any orphan DOM from a prior mount before
    // FormIO writes into the container.
    host.innerHTML = "";

    const options: any = {
      builder: {
        basic: {
          title: "Basic Form Elements",
          weight: 0,
          default: true,
        },
        custom: {
          title: "Custom Elements",
          weight: 10,
        },
        advanced: {
          title: "Advanced",
          weight: 20,
        },
        layout: {
          title: "Layout",
          weight: 30,
        },
        data: {
          title: "Data",
          weight: 40,
        },
        premium: false,
        // Standalone mode: disable loading "Existing Resource Fields" from API
        resource: false,
      },
    };

    let cancelled = false;
    let builder: any = null;

    const initialSchema = formSchemaRef.current ?? {
      display: "form",
      components: [],
    };
    builderSchemaJsonRef.current = JSON.stringify(initialSchema);

    try {
      builder = new (FormioFormBuilder as any)(host, initialSchema, options);
      builderRef.current = builder;

      // v4 builders expose a `ready` promise. We wire up event handlers
      // once the builder finishes its initial render.
      Promise.resolve(builder.ready).then(() => {
        if (cancelled || !builder?.instance) return;
        const wireEvents = (target: any) => {
          if (!target || typeof target.on !== "function") return;
          // `change` fires whenever the builder schema mutates (component
          // added / edited / removed). We sync into React state so
          // save/preview/export see the latest schema.
          //
          // We also update `builderSchemaJsonRef` BEFORE the state
          // update so the external-sync effect (which compares the new
          // formSchema against this ref) sees them as equal and skips
          // calling `builder.setForm(...)` with the same schema. Without
          // this guard every component edit triggered a full builder
          // rebuild, which broke in-flight UI state.
          target.on("change", (form: any) => {
            try {
              const next = form?.form ?? form ?? builder.schema;
              if (!next) return;
              builderSchemaJsonRef.current = JSON.stringify(next);
              setFormSchema(next);
            } catch (err) {
              console.error("[FormBuilder] change handler error", err);
            }
          });
          // Component lifecycle events — we don't need to do anything
          // imperatively here (v4 cleans its own dialog up reliably) but
          // we surface them in DevTools for diagnostics.
          ["saveComponent", "updateComponent", "deleteComponent", "editComponent"].forEach(
            (evt) => {
              target.on(evt, () => {
                // eslint-disable-next-line no-console
                if (typeof window !== "undefined" && (window as any).__doitDebugFormio) {
                  console.log(`[FormBuilder] ${evt}`);
                }
              });
            },
          );
        };
        wireEvents(builder.instance);
        // The webform / form-edit-form sometimes lives on `builder.builder`
        // depending on v4 minor — wire it too so we catch every event.
        wireEvents(builder.builder);
      }).catch((err) => {
        console.error("[FormBuilder] ready promise rejected", err);
      });
    } catch (err) {
      console.error("[FormBuilder] failed to construct FormBuilder", err);
    }

    return () => {
      cancelled = true;
      try {
        builder?.instance?.destroy?.(true);
      } catch (err) {
        console.error("[FormBuilder] destroy error (instance)", err);
      }
      try {
        builder?.destroy?.(true);
      } catch (err) {
        console.error("[FormBuilder] destroy error (builder)", err);
      }
      builderRef.current = null;
      if (host) host.innerHTML = "";
    };
    // Re-mount when the form being edited changes (different formId
    // means a fresh schema must be loaded).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, formId]);

  // Push schema changes from outside (AI assistant, version restore,
  // import, etc.) into the live builder without remounting. The guard
  // compares JSON strings so React's `setFormSchema` echoes (from the
  // builder's own `change` event) don't trigger redundant `setForm`
  // calls.
  useEffect(() => {
    const builder = builderRef.current;
    if (!builder) return;
    try {
      const nextJson = JSON.stringify(formSchema ?? {});
      if (nextJson === builderSchemaJsonRef.current) return;
      builderSchemaJsonRef.current = nextJson;
      Promise.resolve(builder.ready).then(() => {
        try {
          // v4: builder.setForm(schema) replaces the working schema.
          if (typeof builder.setForm === "function") {
            builder.setForm(formSchema);
          } else if (builder.instance && typeof builder.instance.setForm === "function") {
            builder.instance.setForm(formSchema);
          }
        } catch (err) {
          console.error("[FormBuilder] setForm error", err);
        }
      });
    } catch (err) {
      console.error("[FormBuilder] external schema sync error", err);
    }
  }, [formSchema]);

  // ---------------------------------------------------------------------
  // Custom-sidebar → FormIO canvas drag-and-drop bridge
  // ---------------------------------------------------------------------
  // Our React `FormBuilderSidebar` emits HTML5 drag events with the
  // component definition stuffed into `dataTransfer` under the
  // `application/json` MIME type. The drop target is the FormIO builder
  // canvas — but FormIO v4 binds its OWN drop handlers (in bubble phase)
  // deep inside the builder DOM. Those handlers expect FormIO-internal
  // drag payloads and silently swallow / preventDefault unknown drops, so
  // a plain React `onDrop` on an outer wrapper never sees the event when
  // the drop lands inside the canvas.
  //
  // Fix: attach native listeners to the builder host element in
  // CAPTURE phase. Capture descends from the root before bubble runs, so
  // we get the event first. We only swallow it when the payload is ours
  // — drags internal to FormIO (component reordering, etc.) use
  // different MIME types and pass through unchanged.
  useEffect(() => {
    const host = builderHostRef.current;
    if (!host) return;

    const debug = (...args: any[]) => {
      if (typeof window !== "undefined" && (window as any).__doitDebugFormio) {
        // eslint-disable-next-line no-console
        console.log("[FormBuilder/dnd]", ...args);
      }
    };

    const isOurPayload = (e: DragEvent) => {
      const types = e.dataTransfer?.types;
      if (!types) return false;
      // `types` is a DOMStringList in older browsers / array in newer.
      for (let i = 0; i < types.length; i++) {
        if (types[i] === "application/json") return true;
      }
      return false;
    };

    const handleDragOver = (e: DragEvent) => {
      if (!isOurPayload(e)) return;
      e.preventDefault();
      e.stopPropagation();
      if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
    };

    const handleDrop = (e: DragEvent) => {
      if (!isOurPayload(e)) {
        debug("drop ignored — payload types:", Array.from(e.dataTransfer?.types ?? []));
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      const payload = e.dataTransfer?.getData("application/json");
      if (!payload) {
        debug("drop fired but payload was empty");
        return;
      }
      let component: any;
      try {
        component = JSON.parse(payload);
      } catch (err) {
        console.error("[FormBuilder] drop payload parse error", err);
        return;
      }
      debug("drop accepted, component.type =", component?.type);
      addComponentToBuilder(component);
    };

    // Capture phase on the host: runs during the event's descent into
    // FormIO's DOM, before any of FormIO's own (bubble-phase) handlers.
    host.addEventListener("dragover", handleDragOver, true);
    host.addEventListener("drop", handleDrop, true);

    // Backup: document-level capture. Some drag-drop libraries attach at
    // window/document, which would otherwise outrun even our host-level
    // capture handler. The document listener guards on whether the drop
    // landed inside our host before doing anything, so it never
    // interferes with drags outside the builder.
    const docDragOver = (e: DragEvent) => {
      if (!isOurPayload(e)) return;
      const t = e.target as Node | null;
      if (!t || !host.contains(t)) return;
      e.preventDefault();
      e.stopPropagation();
      if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
    };
    const docDrop = (e: DragEvent) => {
      if (!isOurPayload(e)) return;
      const t = e.target as Node | null;
      if (!t || !host.contains(t)) return;
      e.preventDefault();
      e.stopPropagation();
      const payload = e.dataTransfer?.getData("application/json");
      if (!payload) return;
      try {
        const component = JSON.parse(payload);
        debug("doc-level drop accepted, component.type =", component?.type);
        addComponentToBuilder(component);
      } catch (err) {
        console.error("[FormBuilder] doc-level drop parse error", err);
      }
    };
    document.addEventListener("dragover", docDragOver, true);
    document.addEventListener("drop", docDrop, true);

    debug("custom-sidebar drop bridge attached");

    return () => {
      host.removeEventListener("dragover", handleDragOver, true);
      host.removeEventListener("drop", handleDrop, true);
      document.removeEventListener("dragover", docDragOver, true);
      document.removeEventListener("drop", docDrop, true);
    };
    // `addComponentToBuilder` is stable (useCallback with empty deps),
    // so this effect mounts once on initial render and tears down once
    // on unmount. Listed in the dep array only to satisfy the lint rule.
  }, [addComponentToBuilder]);

  // ---------------------------------------------------------------------
  // Preview modal — direct-mount Form via Formio.createForm
  // ---------------------------------------------------------------------
  const previewHostRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!isPreviewOpen) return;
    const host = previewHostRef.current;
    if (!host) return;
    host.innerHTML = "";
    let cancelled = false;
    let formInstance: any = null;
    (Formio as any)
      .createForm(host, formSchema, { readOnly: false })
      .then((instance: any) => {
        if (cancelled) {
          try {
            instance?.destroy?.(true);
          } catch { }
          return;
        }
        formInstance = instance;
        instance.on("submit", (submission: any) => {
          // eslint-disable-next-line no-console
          console.log("Form submission:", submission);
          toast.success("Form submitted (Preview Mode)");
        });
      })
      .catch((err: unknown) => {
        console.error("[FormPreview] createForm failed", err);
      });
    return () => {
      cancelled = true;
      try {
        formInstance?.destroy?.(true);
      } catch { }
      if (host) host.innerHTML = "";
    };
  }, [isPreviewOpen, formSchema]);

  // Expose a manual cleanup for DevTools — still useful when iterating.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const sweep = () => {
      document.querySelectorAll<HTMLElement>(".formio-dialog").forEach((el) => {
        if (!el.querySelector(".formio-dialog-content")) el.remove();
      });
    };
    (window as unknown as { __formioCleanup?: () => void }).__formioCleanup = sweep;
    return () => {
      delete (window as unknown as { __formioCleanup?: () => void }).__formioCleanup;
    };
  }, []);

  // Increment version
  const incrementVersion = (version: string): string => {
    const [major, minor] = version.split(".").map(Number);
    let newMinor = minor + 1;
    let newMajor = major;

    if (newMinor > 9) {
      newMinor = 0;
      newMajor += 1;
    }

    return `${newMajor}.${newMinor}`;
  };

  // Helper to get orgId from token
  const getOrgIdFromToken = (): string | undefined => {
    try {
      const tokenData = localStorage.getItem(
        "oidc.user:https://portal-eroyal.pmdgtech.com/auth/realms/doit:web_app",
      );
      if (tokenData) {
        const parsedData = JSON.parse(tokenData);
        const token = parsedData.access_token;
        if (token) {
          const payload = JSON.parse(atob(token.split(".")[1]));
          // Assuming org_id is an array based on the provided sample, we take the first one
          if (Array.isArray(payload.org_id) && payload.org_id.length > 0) {
            return payload.org_id[0];
          }
          return payload.org_id; // Fallback if it's a string
        }
      }
    } catch (e) {
      console.error("Error parsing token for orgId", e);
    }
    return undefined;
  };

  // Save form
  const handleSave = async () => {
    if (!formName.trim()) {
      toast.error("Please enter a form name");
      return;
    }

    setIsSaving(true);
    try {
      let savedForm: Form;
      const version = existingForm
        ? incrementVersion(existingForm.version || "0.0")
        : "0.1";

      // Generate slug: lowercase, replace spaces with underscores, remove special chars
      const nameSlug = formName
        .trim()
        .toLowerCase()
        .replace(/\s+/g, "_")
        .replace(/[^a-z0-9_]/g, "");

      if (existingForm) {
        // Update existing form
        savedForm = await updateForm(existingForm.id, {
          id: existingForm.id,
          name: formName,
          nameSlug: existingForm.nameSlug || nameSlug,
          description: formDescription,
          version,
        });
      } else {
        // Create new form
        const orgId = getOrgIdFromToken();
        savedForm = await createForm({
          name: formName,
          nameSlug,
          description: formDescription,
          version,
          orgId,
        });
      }

      // Always read the latest schema from the live builder, falling back
      // to React state if the builder isn't mounted yet (shouldn't happen
      // when the user clicks Save, but defensive).
      const liveSchema =
        builderRef.current?.schema ?? builderRef.current?.instance?.schema ?? formSchema;
      const formioJson = btoa(JSON.stringify(liveSchema));
      await saveFormContent({
        nameSlug: savedForm.nameSlug!,
        version: savedForm.version!,
        formioJson,
        formioJsonContentType: "text/json",
        form: savedForm,
      });

      toast.success(
        existingForm
          ? "Form updated successfully"
          : "Form created successfully",
      );
      onSave?.(savedForm);
      onBack();
    } catch (error) {
      console.error("Error saving form:", error);
      toast.error("Failed to save form");
    } finally {
      setIsSaving(false);
    }
  };

  // Import form JSON
  const handleImport = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        const schema = JSON.parse(content);
        setFormSchema(schema);
        toast.success("Form imported successfully");
      } catch (error) {
        console.error("Error importing form:", error);
        toast.error("Failed to import form. Invalid JSON format.");
      }
    };
    reader.readAsText(file);

    // Reset input
    event.target.value = "";
  };

  // Export form JSON
  const handleExport = () => {
    const liveSchema =
      builderRef.current?.schema ?? builderRef.current?.instance?.schema ?? formSchema;
    const blob = new Blob([JSON.stringify(liveSchema, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${formName || "form"}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Form exported successfully");
  };

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="flex items-center gap-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          <span className="text-gray-500">Loading form...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col bg-gray-50 overflow-hidden">
      {/* Compact Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-2.5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={onBack}
              className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
              title="Back to Forms"
            >
              <ArrowLeft size={18} className="text-gray-500" />
            </button>
            <div className="h-5 w-px bg-gray-200" />
            <span className="text-sm font-medium text-gray-900">
              {formName || "Untitled Form"}
            </span>
          </div>

          <div className="flex items-center gap-3">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              accept=".json"
              className="hidden"
            />
            <button
              onClick={() => setIsPreviewOpen(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-purple-600 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100 transition-colors"
            >
              <Eye size={14} />
              Preview
            </button>
            <button
              onClick={onAiClick}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 transition-colors shadow-sm"
              title="AI Assistant"
            >
              <Sparkles size={14} className="text-indigo-500" />
              AI Assistant
            </button>
            {existingForm?.nameSlug && (
              <button
                onClick={() => setIsVersionModalOpen(true)}
                className="flex items-center gap-1.5 text-xs bg-gray-100 hover:bg-gray-200 border border-gray-200 rounded px-2 py-1.5 transition-colors"
                title="Version History"
              >
                <History size={14} className="text-gray-500" />
                <span className="text-gray-700">
                  v{currentVersion || existingForm?.version || "0.1"}
                </span>
              </button>
            )}
            <button
              onClick={handleImport}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <Upload size={14} />
              Import
            </button>
            <button
              onClick={handleExport}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <Download size={14} />
              Export
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 shadow-sm"
            >
              <Save size={14} />
              {isSaving ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      </div>

      {/* Form Builder area. The canvas is always wrapped in the
          `formio-builder-sidebar` class — that CSS hook (see
          FormBuilder.css) hides FormIO's built-in sidebar so our React
          one can take its place. */}
      <div className="flex-1 flex overflow-hidden">
        <FormBuilderSidebar
          onAddComponent={(component) => addComponentToBuilder(component)}
        />

        {/* Form Canvas */}
        <div
          className="flex-1 overflow-auto p-6 formio-builder-sidebar"
          onDragOver={(e) => {
            // Only treat drags carrying our `application/json` payload
            // as ours. Drags internal to FormIO (component reordering)
            // use different payload types and should pass through to
            // FormIO's own handlers untouched.
            const types = e.dataTransfer?.types;
            let hasJson = false;
            if (types) {
              for (let i = 0; i < types.length; i++) {
                if (types[i] === "application/json") {
                  hasJson = true;
                  break;
                }
              }
            }
            if (!hasJson) return;
            e.preventDefault();
            e.dataTransfer.dropEffect = "copy";
          }}
          onDrop={(e) => {
            // Bubble-phase fallback: only fires when the drop lands on
            // padding OUTSIDE the FormIO builder DOM (e.g. the white
            // gutter inside the rounded-xl wrapper). Drops inside the
            // builder are claimed earlier by the capture-phase handler
            // wired up in the dnd-bridge useEffect above.
            const types = e.dataTransfer?.types;
            let hasJson = false;
            if (types) {
              for (let i = 0; i < types.length; i++) {
                if (types[i] === "application/json") {
                  hasJson = true;
                  break;
                }
              }
            }
            if (!hasJson) return;
            e.preventDefault();
            try {
              const componentData = e.dataTransfer.getData("application/json");
              if (componentData) {
                const component = JSON.parse(componentData);
                addComponentToBuilder(component);
              }
            } catch (err) {
              console.error("Error dropping component:", err);
            }
          }}
        >
          <div className="bg-white rounded-xl border border-gray-200 p-4 min-h-[600px]">
            {/* DOM host for the formiojs v4 builder. FormIO writes its
                entire DOM into this element; React must NOT touch the
                children. The effect above owns construction/teardown. */}
            <div ref={builderHostRef} />
          </div>
        </div>
      </div>

      {/* Preview Modal */}
      {isPreviewOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col animate-in fade-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Form Preview
                </h3>
                <p className="text-sm text-gray-500">
                  Test how your form behaves
                </p>
              </div>
              <button
                onClick={() => setIsPreviewOpen(false)}
                className="p-2 text-gray-400 hover:text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              {/* DOM host for the preview Form (formiojs v4
                  Formio.createForm). Mount/teardown lives in the
                  isPreviewOpen effect above. */}
              <div ref={previewHostRef} />
            </div>
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 rounded-b-xl flex justify-end">
              <button
                onClick={() => setIsPreviewOpen(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Close Preview
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Version History Modal */}
      <VersionHistoryModal
        isOpen={isVersionModalOpen}
        onClose={() => setIsVersionModalOpen(false)}
        versions={versions.map((v) => ({
          version: v.version,
          id: v.id,
          createdDate: v.form?.createdDate,
        }))}
        currentVersion={currentVersion || existingForm?.version || "0.1"}
        onPreview={async (version: string) => {
          if (!existingForm?.nameSlug) return;

          toast.loading("Loading version preview...", {
            id: "version-preview",
          });
          try {
            const content = await getFormContent(
              existingForm.nameSlug,
              version,
            );

            if (content?.formioJson) {
              const decodedSchema = JSON.parse(atob(content.formioJson));
              setFormSchema(decodedSchema);
            }

            toast.success(`Previewing version ${version} (unsaved)`, {
              id: "version-preview",
            });
          } catch (error) {
            console.error("Failed to preview:", error);
            toast.error("Failed to load version preview", {
              id: "version-preview",
            });
            throw error;
          }
        }}
        onRevert={async (version: string) => {
          if (!existingForm?.nameSlug) return;

          toast.loading("Restoring version...", { id: "version-revert" });
          try {
            const content = await getFormContent(
              existingForm.nameSlug,
              version,
            );

            if (content?.formioJson) {
              const decodedSchema = JSON.parse(atob(content.formioJson));
              setFormSchema(decodedSchema);
            }

            setCurrentVersion(version);
            setExistingForm((prev) => (prev ? { ...prev, version } : null));
            toast.success(
              `Restored version ${version} - Save to create a new version`,
              { id: "version-revert" },
            );
          } catch (error) {
            console.error("Failed to restore:", error);
            toast.error("Failed to restore version", { id: "version-revert" });
            throw error;
          }
        }}
        title="Form Version History"
      />
    </div>
  );
};

export default FormBuilderPage;
